import ClearIcon from "@mui/icons-material/Clear";
import SearchIcon from "@mui/icons-material/Search";
import {
  Box,
  Button,
  Divider,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { useLocationsState } from "../../containers/LocationsStateContainer";
import { useLayout } from "../../screens/Ramp/CollapsibleRampActionCard";
import { CreateTripDialog } from "../../screens/Schedule/CreateTripDialog/CreateTripDialog";
import { AddTenantButton } from "../../screens/Settings/HangarSettings/AddTenantButton";
import {
  Hangar,
  Location,
  PlacementWithLocation,
  Ramp,
  Shape,
  Stack as StackType,
  Tenant,
  Trip,
} from "../../types";
import { scrollSx } from "../../utils";
import { SELECTION_OPACITY } from "../../utils/selectionStyles";
import { getSpotForPositionLegacy } from "../../utils/spots";
import { TimelineAircraftListItem } from "./TimelineAircraftListItem";

type Props = {
  isAlgorithmTesting?: boolean;
  location: Location<Hangar | Ramp>;
  stack: StackType;
  setStack: (stack: StackType) => void;
  width?: number;
  readOnly?: boolean;
  feetToPixels?: number;
  disableAutoOpen?: boolean;
  // Scheduled view props
  isScheduledView?: boolean;
  onAddTrip?: (trip: Trip) => void;
  currentTime?: Date;
  onRemoveAircraft?: (tenantId: string, tripId: string) => void;
  // Timeline-specific props
  rangeStart: Date;
  rangeEnd: Date;
  placementsInRange: PlacementWithLocation[];
  allPlacementsFromNow: PlacementWithLocation[];
  // Interactive handlers
  onRefresh?: () => void;
  onTimeChange?: (time: Date) => void;
  onNavigateToLocation?: (
    locationId: string,
    locationType: string,
    time: Date
  ) => void;
  // Callback to set the current time (for bulk upload "View Trips" navigation)
  onSetCurrentTime?: (date: Date) => void;
  // Spot detection props
  rampShapes?: Shape[];
  rampDepth?: number;
};

interface AircraftWithTrips {
  tailNumber: string;
  tenantId?: string;
  aircraftModel?: string;
  aircraftMake?: string;
  trips: Array<{
    tripId: string;
    start: Date;
    end: Date;
    locationId: string;
    locationType: "hangar" | "ramp";
    locationName?: string;
    csr_status?: "requested" | "confirmed" | "completed" | "cancelled";
    aircraft_type?: string;
  }>;
  isCurrentlyHere: boolean;
  tenant?: Tenant; // The actual tenant if currently in this location
}

// Wrapper component to properly handle prefilled tail number
const CreateTripDialogWrapper: React.FC<{
  open: boolean;
  onClose: () => void;
  onSuccess: (trip: Trip) => void;
  initialArrivalDate?: Date;
  currentLocationId?: string;
  currentLocationType?: "hangar" | "ramp";
  prefilledTailNumber: string;
}> = (props) => {
  // This wrapper ensures the dialog gets the prefilled tail as initial data
  return (
    <CreateTripDialog
      {...props}
      // We can't directly prefill the tail in CreateTripDialog,
      // but we can use a workaround by using the key prop to force remount
      // The dialog will open with a fresh state each time
    />
  );
};

/**
 * TimelineAircraftList displays aircraft currently at the location by default.
 * When searching:
 * - Searches across ALL trips, all locations, all time
 * - Shows clear location indicators (HERE, NOT HERE, etc.)
 * - Prompts to create a new trip if tail number doesn't exist
 */

export const TimelineAircraftList: React.FC<Props> = ({
  location,
  stack,
  setStack,
  width,
  feetToPixels,
  readOnly = false,
  disableAutoOpen = false,
  isScheduledView = false,
  onAddTrip,
  currentTime = new Date(),
  onRemoveAircraft: _onRemoveAircraft, // Unused but part of interface
  rangeStart,
  rangeEnd,
  placementsInRange,
  allPlacementsFromNow,
  onRefresh,
  onTimeChange,
  onNavigateToLocation,
  onSetCurrentTime,
  rampShapes,
  rampDepth,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { isDrawerOpen } = useLayout();
  const { sendToLocation } = useLocationsState();
  const [filterText, setFilterText] = React.useState("");
  const [showCreateTrip, setShowCreateTrip] = React.useState(false);
  const [prefilledTailNumber, setPrefilledTailNumber] = React.useState("");
  const [createTripKey, setCreateTripKey] = React.useState(0); // Force remount of dialog

  // Ref for scrollable list container (auto-scroll to selected aircraft)
  const listContainerRef = React.useRef<HTMLDivElement>(null);

  // Process placements to get unique aircraft with trip info
  const aircraftWithTrips = React.useMemo(() => {
    const aircraftMap = new Map<string, AircraftWithTrips>();

    // Use ALL placements when searching, otherwise just active
    const placementsToProcess = filterText
      ? allPlacementsFromNow
      : placementsInRange;

    // Process all placements
    placementsToProcess.forEach((placement) => {
      // Skip cancelled placements when not searching
      if (placement.status === "cancelled" && !filterText) {
        return;
      }

      // Get tail number from placement data - it's directly on the placement
      const tailNumber = placement.tail_number;

      if (!tailNumber) return;

      if (!aircraftMap.has(tailNumber)) {
        aircraftMap.set(tailNumber, {
          tailNumber,
          tenantId: placement.tenant_id,
          aircraftModel: placement.aircraft_type, // Using aircraft_type field
          aircraftMake: placement.aircraft_make, // Add make from placement
          trips: [],
          isCurrentlyHere: false,
          tenant: undefined,
        });
      }

      const aircraft = aircraftMap.get(tailNumber)!;

      // Check if we already have this trip (by trip_id)
      const existingTripIndex = aircraft.trips.findIndex(
        (t) => t.tripId === placement.trip_id
      );

      if (existingTripIndex >= 0) {
        // Update existing trip with broader time range
        const existingTrip = aircraft.trips[existingTripIndex];
        const placementStart = new Date(placement.start_time);
        const placementEnd = new Date(placement.end_time);

        // Expand the time range to include this placement
        if (placementStart < existingTrip.start) {
          existingTrip.start = placementStart;
        }
        if (placementEnd > existingTrip.end) {
          existingTrip.end = placementEnd;
        }

        // Update location if this placement is more relevant (e.g., matches current time)
        const now = currentTime.getTime();
        const placementStartTime = placementStart.getTime();
        const placementEndTime = placementEnd.getTime();

        if (placementStartTime <= now && placementEndTime >= now) {
          // This placement is current, use its location
          existingTrip.locationId =
            placement.hangar_id || placement.ramp_id || "";
          existingTrip.locationType = placement.hangar_id ? "hangar" : "ramp";
          existingTrip.locationName = placement.location_name;
        }

        // Always update csr_status and trip type from the placement
        existingTrip.csr_status = placement.csr_status || "requested";
        existingTrip.aircraft_type = placement.aircraft_type;
      } else {
        // Add as new trip
        aircraft.trips.push({
          tripId: placement.trip_id,
          start: new Date(placement.start_time),
          end: new Date(placement.end_time),
          locationId: placement.hangar_id || placement.ramp_id || "",
          locationType: placement.hangar_id ? "hangar" : "ramp",
          locationName: placement.location_name,
          csr_status: placement.csr_status || "requested",
          aircraft_type: placement.aircraft_type,
        });
      }

      // Check if currently at this location (exclude cancelled trips)
      const now = currentTime.getTime();
      const startTime = new Date(placement.start_time).getTime();
      const endTime = new Date(placement.end_time).getTime();

      if (
        startTime <= now &&
        endTime >= now &&
        placement.status !== "cancelled"
      ) {
        const isAtThisLocation =
          (location.type === "hangar" && placement.hangar_id === location.id) ||
          (location.type === "ramp" && placement.ramp_id === location.id);

        if (isAtThisLocation) {
          aircraft.isCurrentlyHere = true;
          // Find the matching tenant from the stack
          aircraft.tenant = stack.tenants.find(
            (t) => t.tail_number === tailNumber
          );
        }
      }
    });

    // Filter based on search mode
    const allAircraft = Array.from(aircraftMap.values());
    let filteredByView = allAircraft;

    // When not searching, show only active aircraft at this location
    if (!filterText) {
      filteredByView = allAircraft.filter((a) => a.isCurrentlyHere);
    }
    // When searching, show ALL aircraft (already using allPlacementsFromNow)

    // Sort alphabetically by: (1) tail number, (2) make, (3) model
    return filteredByView.sort((a, b) => {
      // 1. Compare tail numbers (case-insensitive)
      const tailCompare = a.tailNumber.localeCompare(b.tailNumber, undefined, {
        sensitivity: "base",
      });
      if (tailCompare !== 0) return tailCompare;

      // 2. Compare makes if both exist
      if (a.aircraftMake && b.aircraftMake) {
        const makeCompare = a.aircraftMake.localeCompare(
          b.aircraftMake,
          undefined,
          { sensitivity: "base" }
        );
        if (makeCompare !== 0) return makeCompare;
      } else if (a.aircraftMake) return -1;
      else if (b.aircraftMake) return 1;

      // 3. Compare models if both exist
      if (a.aircraftModel && b.aircraftModel) {
        return a.aircraftModel.localeCompare(b.aircraftModel, undefined, {
          sensitivity: "base",
        });
      } else if (a.aircraftModel) return -1;
      else if (b.aircraftModel) return 1;

      return 0;
    });
  }, [
    placementsInRange,
    allPlacementsFromNow,
    currentTime,
    location,
    stack.tenants,
    filterText,
  ]);

  // Filter aircraft based on search text and check if tail exists
  const { filteredAircraft, tailNumberExists } = React.useMemo(() => {
    if (!filterText) {
      return { filteredAircraft: aircraftWithTrips, tailNumberExists: true };
    }

    const searchText = filterText.toLowerCase().trim();
    const filtered = aircraftWithTrips.filter((aircraft) =>
      aircraft.tailNumber.toLowerCase().includes(searchText)
    );

    // Check for exact match to determine if tail number exists
    const exactMatch = aircraftWithTrips.some(
      (aircraft) =>
        aircraft.tailNumber.toLowerCase() === searchText ||
        aircraft.tailNumber.toLowerCase() === "n" + searchText
    );

    return {
      filteredAircraft: filtered,
      tailNumberExists: exactMatch || filtered.length > 0,
    };
  }, [aircraftWithTrips, filterText]);

  const onAddAircraft = React.useCallback(
    (tenant: Tenant | Tenant[]) => {
      if (Array.isArray(tenant)) {
        setStack({
          ...stack,
          tenants: [...tenant, ...stack.tenants],
        });
        return;
      }
      setStack({
        ...stack,
        tenants: [tenant, ...stack.tenants],
      });
    },
    [stack, setStack]
  );

  const setTenant = React.useCallback(
    (tenant: Tenant) => {
      setStack({
        ...stack,
        tenants: stack.tenants.map((t) => (t.id === tenant.id ? tenant : t)),
      });
    },
    [stack, setStack]
  );

  const onDeleteAircraft = React.useCallback(
    (tenant: Tenant) => {
      setStack({
        ...stack,
        tenants: stack.tenants.filter(
          (t) => t.placement_id !== tenant.placement_id
        ),
      });
    },
    [stack, setStack]
  );

  const onSelectAircraft = React.useCallback(
    (tenant: Tenant) => {
      setStack({
        ...stack,
        tenants: stack.tenants.map((t) => ({
          ...t,
          selected:
            t.placement_id === tenant.placement_id ? !t.selected : false,
        })),
      });
    },
    [stack, setStack]
  );

  // Calculate total aircraft count
  const totalAircraft = aircraftWithTrips.length;
  const currentlyHereCount = aircraftWithTrips.filter(
    (a) => a.isCurrentlyHere
  ).length;
  const filteredHereCount = filteredAircraft.filter(
    (a) => a.isCurrentlyHere
  ).length;

  // Handle create trip for non-existent tail number
  const handleCreateTripClick = React.useCallback(() => {
    // Normalize the tail number (add 'N' prefix if not present)
    const normalizedTail = filterText.toUpperCase().startsWith("N")
      ? filterText.toUpperCase()
      : "N" + filterText.toUpperCase();
    setPrefilledTailNumber(normalizedTail);
    setCreateTripKey((prev) => prev + 1); // Force dialog remount
    setShowCreateTrip(true);
  }, [filterText]);

  const handleTripCreated = React.useCallback(() => {
    // Refresh the view after trip creation
    if (onRefresh) {
      onRefresh();
    }
    setShowCreateTrip(false);
    setFilterText("");
  }, [onRefresh]);

  // Auto-scroll to selected aircraft when selection changes
  React.useEffect(() => {
    const selectedTenant = stack.tenants.find((t) => t.selected);
    if (!selectedTenant || !listContainerRef.current) return;

    // Find the element by data attribute
    const selectedElement = listContainerRef.current.querySelector(
      `[data-placement-id="${selectedTenant.placement_id}"]`
    );

    if (selectedElement) {
      selectedElement.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    }
  }, [stack.tenants]);

  // Calculate spot for a tenant based on current position
  const calculateSpotForTenant = React.useCallback(
    (tenant: Tenant): Shape | undefined => {
      if (!rampShapes || !rampDepth || !tenant.position) {
        return undefined;
      }

      // Filter for spot shapes only
      const spotShapes = rampShapes.filter((shape) => shape.type === "Spot");
      if (spotShapes.length === 0) {
        return undefined;
      }

      // Build a map of legacy spots to their shape IDs
      const shapeIdMap = new Map<any, string>();

      // Convert spot shapes to LegacySpot format
      const legacySpots = spotShapes
        .filter(
          (shape) => shape.reference_points && shape.reference_points.length > 0
        )
        .map((shape) => {
          const legacySpot = {
            points: shape.points,
            referencePoints: shape.reference_points!,
          };
          shapeIdMap.set(legacySpot, shape.id);
          return legacySpot;
        });

      if (legacySpots.length === 0) {
        return undefined;
      }

      // In ScheduledRampView, tenant positions come from database placements
      // which use Y-flipped coordinates (origin at top-left)
      // Spot shapes use non-flipped coordinates (origin at bottom-left)
      // Apply Y-flip transformation to match coordinate systems
      const position = {
        x: tenant.position.x,
        y: rampDepth - tenant.position.y,
        angle: tenant.position.angle,
      };

      // Call getSpotForPositionLegacy
      try {
        const result = getSpotForPositionLegacy(tenant, position, legacySpots);

        if (!result) {
          return undefined;
        }

        // Get the shape ID from the map
        const shapeId = shapeIdMap.get(result.spot);
        if (!shapeId) {
          return undefined;
        }

        // Find and return the matching shape
        return spotShapes.find((shape) => shape.id === shapeId);
      } catch (error) {
        console.error("Error detecting spot for tenant:", error);
        return undefined;
      }
    },
    [rampShapes, rampDepth]
  );

  if (!isDrawerOpen) {
    return (
      <Stack
        direction="column"
        alignItems="center"
        spacing={2}
        sx={{
          width: "100%",
          height: "100%",
          overflow: "hidden",
        }}
      >
        <AddTenantButton
          iconButton
          disabled={readOnly}
          onAdd={onAddAircraft}
          location={location}
          sendToLocation={sendToLocation}
          stack={stack}
          setStack={setStack}
          feetToPixels={feetToPixels}
          disableAutoOpen={disableAutoOpen}
          isScheduledView={isScheduledView}
          onAddTrip={onAddTrip}
          currentTime={currentTime}
          onSetCurrentTime={onSetCurrentTime}
        />
        <Typography
          variant="caption"
          sx={{ color: theme.palette.primary.main }}
        >
          {filterText
            ? t('widgets.timelineAircraftList.countOfTotal', { filtered: filteredAircraft.length, total: totalAircraft })
            : t('widgets.timelineAircraftList.aircraftCount', { count: totalAircraft })}
        </Typography>
        <TextField
          size="small"
          placeholder={t('widgets.timelineAircraftList.filterPlaceholder')}
          value={filterText}
          onChange={(e) => setFilterText(e.target.value)}
          sx={{
            width: "90%",
            "& .MuiInputBase-input": {
              fontSize: "0.75rem",
              py: 0.5,
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: "1rem" }} />
              </InputAdornment>
            ),
            endAdornment: filterText && (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={() => setFilterText("")}
                  sx={{ p: 0 }}
                >
                  <ClearIcon sx={{ fontSize: "1rem" }} />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <Divider flexItem />
        <Stack
          direction="column"
          alignItems="center"
          sx={{
            overflowY: "scroll",
            overflowX: "hidden",
            width: "100%",
            ...scrollSx,
          }}
        >
          {filteredAircraft.map((aircraft, idx) => (
            <Box
              key={`timeline-aircraft-${aircraft.tailNumber}-${idx}`}
              sx={{
                width: "100%",
                p: 0.5,
                cursor: "pointer",
                bgcolor: aircraft.tenant?.selected
                  ? `${theme.palette.success.main}${SELECTION_OPACITY.selected}`
                  : aircraft.isCurrentlyHere
                  ? `${theme.palette.primary.main}08`
                  : "transparent",
                "&:hover": {
                  backgroundColor: `${theme.palette.success.main}${SELECTION_OPACITY.hover}`,
                },
              }}
              onClick={() =>
                aircraft.tenant && onSelectAircraft(aircraft.tenant)
              }
            >
              <Typography
                variant="caption"
                noWrap
                sx={{
                  fontWeight: aircraft.isCurrentlyHere ? 600 : 400,
                  color: aircraft.isCurrentlyHere
                    ? theme.palette.primary.main
                    : "inherit",
                }}
              >
                {aircraft.tailNumber.substring(0, 6)}
              </Typography>
              {aircraft.trips.length > 1 && (
                <Typography
                  variant="caption"
                  sx={{
                    ml: 0.5,
                    fontSize: "0.65rem",
                    color: theme.palette.text.secondary,
                  }}
                >
                  ({aircraft.trips.length})
                </Typography>
              )}
            </Box>
          ))}
        </Stack>
      </Stack>
    );
  }

  return (
    <>
      <Stack
        direction="column"
        spacing={2}
        sx={{
          minWidth: width ?? 400,
          height: "100%",
          overflow: "hidden",
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ px: 1.5, py: 1 }}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography>{t('widgets.timelineAircraftList.title')}</Typography>
            <Typography
              variant="caption"
              sx={{ color: theme.palette.text.secondary }}
            >
              ({filterText
                ? t('widgets.timelineAircraftList.filteredAndHere', {
                    filtered: filteredAircraft.length,
                    total: totalAircraft,
                    filteredHere: filteredHereCount
                  })
                : t('widgets.timelineAircraftList.totalAndHere', {
                    total: totalAircraft,
                    here: currentlyHereCount
                  })})
            </Typography>
          </Stack>
          <AddTenantButton
            disabled={readOnly}
            onAdd={onAddAircraft}
            location={location}
            sendToLocation={sendToLocation}
            stack={stack}
            setStack={setStack}
            feetToPixels={feetToPixels}
            isScheduledView={isScheduledView}
            onAddTrip={onAddTrip}
            currentTime={currentTime}
            onSetCurrentTime={onSetCurrentTime}
          />
        </Stack>
        <Box sx={{ px: 1.5, pb: 1 }}>
          <TextField
            fullWidth
            size="small"
            placeholder={t('widgets.timelineAircraftList.searchPlaceholder')}
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: filterText && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => setFilterText("")}
                    edge="end"
                  >
                    <ClearIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Box>
        {/* Show create trip prompt when tail number not found */}
        {filterText && !tailNumberExists && (
          <Box sx={{ px: 1.5, pb: 1 }}>
            <Stack
              direction="column"
              alignItems="center"
              spacing={1}
              sx={{
                p: 2,
                bgcolor: theme.palette.action.hover,
                borderRadius: 1,
              }}
            >
              <Typography
                variant="body2"
                sx={{ color: theme.palette.text.secondary }}
              >
                {t('widgets.timelineAircraftList.noAircraftFound', { tailNumber: filterText.toUpperCase() })}
              </Typography>
              <Button
                size="small"
                variant="contained"
                color="primary"
                onClick={handleCreateTripClick}
              >
                {t('widgets.timelineAircraftList.createNewTrip')}
              </Button>
            </Stack>
          </Box>
        )}
        <Box
          ref={listContainerRef}
          sx={{
            flex: 1,
            height: "100%",
            width: "100%",
            overflowY: "scroll",
            overflowX: "hidden",
            ...scrollSx,
          }}
        >
          {filteredAircraft.map((aircraft, idx) => {
            // Find current placement for this aircraft (if HERE NOW)
            const currentPlacement = aircraft.isCurrentlyHere
              ? placementsInRange.find(
                  (p) =>
                    p.tail_number === aircraft.tailNumber &&
                    new Date(p.start_time).getTime() <= currentTime.getTime() &&
                    new Date(p.end_time).getTime() >= currentTime.getTime() &&
                    ((location.type === "ramp" && p.ramp_id === location.id) ||
                      (location.type === "hangar" && p.hangar_id === location.id))
                )
              : undefined;

            // Calculate spot from current tenant position
            const calculatedSpot = aircraft.tenant
              ? calculateSpotForTenant(aircraft.tenant)
              : undefined;

            // Merge calculated spot into placement (override database spot with live calculation)
            const enhancedPlacement = currentPlacement
              ? { ...currentPlacement, spot: calculatedSpot }
              : undefined;

            return (
              <Box
                key={`timeline-aircraft-${aircraft.tailNumber}-${idx}`}
                data-placement-id={aircraft.tenant?.placement_id}
              >
                <TimelineAircraftListItem
                  aircraft={aircraft}
                  tenant={aircraft.tenant}
                  stack={stack}
                  setStack={setStack}
                  setTenant={setTenant}
                  onSelectAircraft={onSelectAircraft}
                  onDeleteAircraft={onDeleteAircraft}
                  showBottomBorder={idx + 1 === filteredAircraft.length}
                  rangeStart={rangeStart}
                  rangeEnd={rangeEnd}
                  currentTime={currentTime}
                  location={location}
                  onRefresh={onRefresh}
                  onTimeChange={onTimeChange}
                  onNavigateToLocation={onNavigateToLocation}
                  currentPlacement={enhancedPlacement}
                />
              </Box>
            );
          })}
        </Box>
      </Stack>
      {/* Create Trip Dialog with prefilled tail number */}
      {showCreateTrip && (
        <CreateTripDialogWrapper
          key={createTripKey}
          open={showCreateTrip}
          onClose={() => {
            setShowCreateTrip(false);
            setPrefilledTailNumber("");
          }}
          onSuccess={handleTripCreated}
          initialArrivalDate={currentTime}
          currentLocationId={location.id}
          currentLocationType={location.type as "hangar" | "ramp"}
          prefilledTailNumber={prefilledTailNumber}
        />
      )}
    </>
  );
};
